import React, { useState, useEffect } from 'react';
import { 
  MapPin, Calendar, Sparkles, Mail, Share2, DollarSign, Menu, X,
  Plane, CheckCircle2, Globe, Shield, ChevronDown, ChevronUp,
  Clock, Zap, FileText, Map, Star, ArrowRight
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import RotatingBackground from '../components/RotatingBackground';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const LandingPage = () => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 686);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogin = () => {
    navigate('/login');
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <RotatingBackground showCaption={true} interval={8000}>
      {/* Navigation */}
      <nav className="fixed left-0 right-0 z-50 bg-black/20 backdrop-blur-md border-b border-white/10" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-7xl mx-auto px-4 compact:px-6 py-3 compact:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="text-white" size={isMobile ? 24 : 28} />
            <span className="text-xl compact:text-2xl font-bold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>{t('appName')}</span>
          </div>
          
          <div className="hidden compact:flex items-center gap-6">
            <a href="#features" className="text-sm text-white/80 hover:text-white transition-colors" style={{fontFamily: 'Inter, sans-serif'}}>Features</a>
            <a href="#how-it-works" className="text-sm text-white/80 hover:text-white transition-colors" style={{fontFamily: 'Inter, sans-serif'}}>How It Works</a>
            <a href="#faq" className="text-sm text-white/80 hover:text-white transition-colors" style={{fontFamily: 'Inter, sans-serif'}}>FAQ</a>
            <Button 
              onClick={handleLogin}
              data-testid="landing-login-btn"
              className="bg-[var(--md3-primary)] hover:bg-[var(--md3-primary-hover)] text-white font-semibold shadow-lg px-6 py-2 rounded-full transition-all duration-300 hover:scale-105"
            >
              Sign In
            </Button>
          </div>

          <Button
            onClick={() => setMenuOpen(!menuOpen)}
            variant="ghost"
            size="sm"
            className="compact:hidden text-white hover:bg-white/10"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>

        {menuOpen && (
          <div className="compact:hidden bg-black/40 backdrop-blur-md border-t border-white/10 px-4 py-4 space-y-3">
            <a href="#features" onClick={() => setMenuOpen(false)} className="block text-white/80 hover:text-white text-sm py-1" style={{fontFamily: 'Inter, sans-serif'}}>Features</a>
            <a href="#how-it-works" onClick={() => setMenuOpen(false)} className="block text-white/80 hover:text-white text-sm py-1" style={{fontFamily: 'Inter, sans-serif'}}>How It Works</a>
            <a href="#faq" onClick={() => setMenuOpen(false)} className="block text-white/80 hover:text-white text-sm py-1" style={{fontFamily: 'Inter, sans-serif'}}>FAQ</a>
            <Button 
              onClick={handleLogin}
              data-testid="mobile-login-btn"
              className="w-full bg-[var(--md3-primary)] hover:bg-[var(--md3-primary-hover)] text-white font-semibold shadow-lg py-3 rounded-full"
            >
              Sign In
            </Button>
          </div>
        )}
      </nav>

      {/* ============================================ */}
      {/* HERO SECTION */}
      {/* ============================================ */}
      <div className="pt-28 compact:pt-36 pb-16 compact:pb-24 px-4 compact:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 compact:mb-16">
            <h1 
              className="text-4xl compact:text-5xl wide:text-6xl font-bold text-white mb-4 compact:mb-6 leading-tight drop-shadow-2xl"
              style={{fontFamily: 'Space Grotesk, sans-serif'}}
            >
              {t('planYourPerfect')}
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--md3-primary)] to-cyan-400">
                {t('journey')}
              </span>
            </h1>
            <p className="text-base compact:text-lg text-white/90 mb-8 compact:mb-10 max-w-2xl mx-auto drop-shadow-lg px-4 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
              iGo is the AI-powered travel planner that turns your booking confirmation emails into a beautifully organized itinerary. 
              Plan smarter, travel better, and keep everything in one place.
            </p>
            <div className="flex flex-col compact:flex-row items-center justify-center gap-4">
              <Button 
                onClick={handleLogin}
                data-testid="hero-get-started-btn"
                className="bg-gradient-to-r from-[var(--md3-primary)] to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white text-base compact:text-lg px-8 compact:px-10 py-5 compact:py-6 rounded-full transition-all duration-300 hover:scale-105 shadow-2xl shadow-blue-500/50 w-full compact:w-auto"
              >
                Get Started Free
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <p className="text-sm text-white/60" style={{fontFamily: 'Inter, sans-serif'}}>No credit card required</p>
            </div>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap items-center justify-center gap-6 compact:gap-10 mt-8 compact:mt-12">
            <div className="flex items-center gap-2 text-white/70">
              <Shield size={18} className="text-green-400" />
              <span className="text-sm" style={{fontFamily: 'Inter, sans-serif'}}>Google Sign-In</span>
            </div>
            <div className="flex items-center gap-2 text-white/70">
              <Globe size={18} className="text-cyan-400" />
              <span className="text-sm" style={{fontFamily: 'Inter, sans-serif'}}>Works Worldwide</span>
            </div>
            <div className="flex items-center gap-2 text-white/70">
              <Zap size={18} className="text-yellow-400" />
              <span className="text-sm" style={{fontFamily: 'Inter, sans-serif'}}>AI-Powered</span>
            </div>
            <div className="flex items-center gap-2 text-white/70">
              <Plane size={18} className="text-sky-400" />
              <span className="text-sm" style={{fontFamily: 'Inter, sans-serif'}}>All Trip Types</span>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* FEATURE OVERVIEW GRID */}
      {/* ============================================ */}
      <section id="features" className="py-16 compact:py-24 px-4 compact:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 compact:mb-16">
            <h2 className="text-2xl compact:text-3xl wide:text-4xl font-bold text-white mb-4 drop-shadow-xl" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Everything You Need to Plan the Perfect Trip
            </h2>
            <p className="text-base compact:text-lg text-white/80 max-w-3xl mx-auto leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
              From the moment you book your first flight to the day you return home, iGo keeps every detail 
              organized, every reservation tracked, and every destination mapped. Here is what makes iGo 
              the travel planner that frequent travelers and vacation planners actually stick with.
            </p>
          </div>

          <div className="grid grid-cols-1 compact:grid-cols-2 wide:grid-cols-3 gap-4 compact:gap-6 wide:gap-8">
            <FeatureCard 
              icon={<Mail className="text-[var(--md3-primary)]" size={isMobile ? 28 : 32} />}
              title="Smart Email Parsing"
              description="Forward your booking confirmation emails and our AI instantly extracts flight numbers, hotel addresses, reservation dates, confirmation codes, and costs. No more manual data entry."
              isMobile={isMobile}
            />
            <FeatureCard 
              icon={<Sparkles className="text-cyan-400" size={isMobile ? 28 : 32} />}
              title="AI Trip Assistant"
              description="Ask our AI for personalized restaurant recommendations, activity suggestions, budget tips, and packing lists tailored to your specific destinations, dates, and travel style."
              isMobile={isMobile}
            />
            <FeatureCard 
              icon={<MapPin className="text-[var(--md3-primary)]" size={isMobile ? 28 : 32} />}
              title="Interactive Maps"
              description="See your entire trip on an interactive Google Map with color-coded markers for flights, hotels, restaurants, and activities. Visualize flight paths, train routes, and cruise itineraries."
              isMobile={isMobile}
            />
            <FeatureCard 
              icon={<Calendar className="text-cyan-400" size={isMobile ? 28 : 32} />}
              title="Organized Itinerary"
              description="Drag and drop to reorder your plans. Every booking type is supported: flights, hotels, vacation rentals, trains, cruises, restaurants, activities, and car rentals."
              isMobile={isMobile}
            />
            <FeatureCard 
              icon={<Share2 className="text-[var(--md3-primary)]" size={isMobile ? 28 : 32} />}
              title="Share With Anyone"
              description="Generate a shareable link for your trip and send it to travel companions, family, or friends. They can view your full itinerary and map without needing an account."
              isMobile={isMobile}
            />
            <FeatureCard 
              icon={<DollarSign className="text-cyan-400" size={isMobile ? 28 : 32} />}
              title="Budget Tracking"
              description="Set a trip budget and track spending across categories: flights, accommodation, dining, and activities. See exactly how much you have spent and what remains at a glance."
              isMobile={isMobile}
            />
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* HOW IT WORKS */}
      {/* ============================================ */}
      <section id="how-it-works" className="py-16 compact:py-24 px-4 compact:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 compact:mb-16">
            <h2 className="text-2xl compact:text-3xl wide:text-4xl font-bold text-white mb-4 drop-shadow-xl" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              How iGo Works
            </h2>
            <p className="text-base compact:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
              Get from scattered booking emails to a complete, organized trip plan in three simple steps.
            </p>
          </div>

          <div className="grid grid-cols-1 wide:grid-cols-3 gap-8 compact:gap-10">
            <StepCard
              number="1"
              icon={<Plane size={28} />}
              title="Create Your Trip"
              description="Start by naming your trip and adding your destinations. Whether it is a weekend getaway to Paris, a two-week road trip across Italy, or a multi-city business trip, iGo handles it all. Set your travel dates and budget to get started."
            />
            <StepCard
              number="2"
              icon={<Mail size={28} />}
              title="Import Your Bookings"
              description="Connect your Gmail account and iGo automatically scans for booking confirmation emails from airlines, hotels, and rental companies. Our AI extracts every detail: flight numbers, check-in times, confirmation codes, costs, and addresses. You can also paste email text directly."
            />
            <StepCard
              number="3"
              icon={<Map size={28} />}
              title="Visualize and Optimize"
              description="Your itinerary appears on an interactive map with all your bookings plotted as markers. See flight paths, train routes, and cruise itineraries. Use the AI assistant to fill gaps with restaurant recommendations, activity suggestions, and local tips for each destination."
            />
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* DETAILED FEATURE DEEP DIVES */}
      {/* ============================================ */}
      <section className="py-16 compact:py-24 px-4 compact:px-6">
        <div className="max-w-7xl mx-auto space-y-20 compact:space-y-28">
          
          {/* Feature 1: Email Parsing */}
          <div className="grid grid-cols-1 wide:grid-cols-2 gap-8 wide:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[var(--md3-primary)]/20 border border-[var(--md3-primary)]/30 rounded-full px-4 py-1.5 mb-4">
                <Mail size={16} className="text-[var(--md3-primary)]" />
                <span className="text-sm font-medium text-[var(--md3-primary)]" style={{fontFamily: 'Inter, sans-serif'}}>AI Email Parsing</span>
              </div>
              <h3 className="text-2xl compact:text-3xl font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Your Booking Emails, Automatically Organized
              </h3>
              <p className="text-white/80 mb-6 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
                Stop copying and pasting confirmation details into spreadsheets. iGo connects to your Gmail account and 
                uses advanced AI to scan your inbox for travel booking confirmations. It recognizes emails from major airlines 
                like American Airlines, Delta, United, and Southwest, as well as hotel chains, Airbnb, VRBO, Amtrak, 
                cruise lines, and car rental companies.
              </p>
              <p className="text-white/80 mb-6 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
                For each booking, the AI extracts the confirmation number, travel dates and times, departure and arrival 
                locations, passenger names, seat assignments, and total cost. Multi-passenger bookings are automatically 
                split into individual entries so every traveler has their own record.
              </p>
              <ul className="space-y-3">
                {[
                  'Supports flights, hotels, trains, cruises, car rentals, and vacation rentals',
                  'Extracts confirmation numbers, dates, costs, and locations',
                  'Multi-passenger bookings are split automatically',
                  'Works with Gmail integration or paste email text directly'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/80">
                    <CheckCircle2 size={18} className="text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm" style={{fontFamily: 'Inter, sans-serif'}}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-black/30 backdrop-blur-md border border-white/10 rounded-2xl p-6 compact:p-8">
              <div className="space-y-4">
                <div className="flex items-center gap-3 bg-white/5 rounded-xl p-4">
                  <Plane size={20} className="text-sky-400 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium text-sm" style={{fontFamily: 'Inter, sans-serif'}}>American Airlines MIA to FCO</p>
                    <p className="text-white/60 text-xs" style={{fontFamily: 'Inter, sans-serif'}}>Confirmation: XKWP2R | Mar 15, 2026 | $847</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white/5 rounded-xl p-4">
                  <FileText size={20} className="text-rose-400 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium text-sm" style={{fontFamily: 'Inter, sans-serif'}}>Hotel Palazzo Roma</p>
                    <p className="text-white/60 text-xs" style={{fontFamily: 'Inter, sans-serif'}}>Check-in: Mar 15 | Check-out: Mar 20 | $620</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white/5 rounded-xl p-4">
                  <Clock size={20} className="text-emerald-400 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium text-sm" style={{fontFamily: 'Inter, sans-serif'}}>Trenitalia Rome to Florence</p>
                    <p className="text-white/60 text-xs" style={{fontFamily: 'Inter, sans-serif'}}>Mar 20, 2026 09:30 AM | Seat 42A | $38</p>
                  </div>
                </div>
                <div className="text-center pt-2">
                  <p className="text-white/50 text-xs italic" style={{fontFamily: 'Inter, sans-serif'}}>Extracted automatically from booking emails</p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 2: AI Assistant */}
          <div className="grid grid-cols-1 wide:grid-cols-2 gap-8 wide:gap-16 items-center">
            <div className="order-2 wide:order-1 bg-black/30 backdrop-blur-md border border-white/10 rounded-2xl p-6 compact:p-8">
              <div className="space-y-4">
                <div className="bg-[var(--md3-primary)]/10 border border-[var(--md3-primary)]/20 rounded-xl p-4">
                  <p className="text-white/90 text-sm" style={{fontFamily: 'Inter, sans-serif'}}>
                    "What are the best restaurants near the Colosseum that locals actually go to? I prefer authentic Italian food, not tourist traps."
                  </p>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="text-white/90 text-sm leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
                    Great question! Here are some highly-rated local favorites near the Colosseum:
                  </p>
                  <div className="mt-3 space-y-2">
                    <p className="text-white/80 text-sm" style={{fontFamily: 'Inter, sans-serif'}}><span className="font-semibold text-cyan-400">Trattoria Luzzi</span> - Family-run since 1945, famous for their cacio e pepe and amatriciana. Just a 3-minute walk from the Colosseum.</p>
                    <p className="text-white/80 text-sm" style={{fontFamily: 'Inter, sans-serif'}}><span className="font-semibold text-cyan-400">Ristorante Aroma</span> - Upscale dining with a terrace overlooking the Colosseum. Their tasting menu is unforgettable.</p>
                  </div>
                </div>
                <div className="text-center pt-2">
                  <p className="text-white/50 text-xs italic" style={{fontFamily: 'Inter, sans-serif'}}>AI responses are personalized to your trip destinations</p>
                </div>
              </div>
            </div>
            <div className="order-1 wide:order-2">
              <div className="inline-flex items-center gap-2 bg-cyan-500/20 border border-cyan-500/30 rounded-full px-4 py-1.5 mb-4">
                <Sparkles size={16} className="text-cyan-400" />
                <span className="text-sm font-medium text-cyan-400" style={{fontFamily: 'Inter, sans-serif'}}>AI Travel Assistant</span>
              </div>
              <h3 className="text-2xl compact:text-3xl font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Your Personal Travel Expert, Available 24/7
              </h3>
              <p className="text-white/80 mb-6 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
                iGo includes a built-in AI travel assistant powered by Google Gemini that knows your trip inside 
                and out. It reads your destinations, dates, existing bookings, and budget to give you truly 
                personalized recommendations, not generic travel advice you could find on any blog.
              </p>
              <p className="text-white/80 mb-6 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
                Ask for restaurant suggestions near your hotel, activity ideas for a free afternoon in Barcelona, 
                tips on optimizing your itinerary route to minimize transit time, or a packing list customized 
                for your specific destinations and weather. The AI considers everything in your trip when 
                generating its answers.
              </p>
              <ul className="space-y-3">
                {[
                  'Personalized restaurant and activity recommendations',
                  'Itinerary optimization for minimal travel time',
                  'Destination-specific budget tips and cost-saving ideas',
                  'Custom packing lists based on your activities and weather',
                  'Open-ended chat for any travel question'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/80">
                    <CheckCircle2 size={18} className="text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm" style={{fontFamily: 'Inter, sans-serif'}}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Feature 3: Maps & Budget */}
          <div className="grid grid-cols-1 wide:grid-cols-2 gap-8 wide:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-green-500/20 border border-green-500/30 rounded-full px-4 py-1.5 mb-4">
                <Map size={16} className="text-green-400" />
                <span className="text-sm font-medium text-green-400" style={{fontFamily: 'Inter, sans-serif'}}>Maps & Planning</span>
              </div>
              <h3 className="text-2xl compact:text-3xl font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                See Your Entire Trip at a Glance
              </h3>
              <p className="text-white/80 mb-6 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
                Every booking and activity is automatically plotted on a full-screen interactive Google Map. 
                Color-coded markers distinguish between flights, hotels, restaurants, and activities so you 
                can instantly see the geographic layout of your trip. Flight paths appear as arcs across the 
                map, train routes follow the rail lines, and cruise itineraries show your port stops connected 
                by sea routes.
              </p>
              <p className="text-white/80 mb-6 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
                Click any marker to see booking details, get directions, or open the location in Google Maps. 
                When you select a destination city, iGo automatically loads the top-rated local attractions, 
                restaurants, current weather, and city photos to help you discover what each destination has 
                to offer before you arrive.
              </p>
              <ul className="space-y-3">
                {[
                  'Color-coded markers for every booking type',
                  'Flight paths, train routes, and cruise sea routes on the map',
                  'Click-to-discover local attractions and restaurants',
                  'Real-time weather forecasts for each destination',
                  'Export your entire itinerary to Google Calendar'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/80">
                    <CheckCircle2 size={18} className="text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm" style={{fontFamily: 'Inter, sans-serif'}}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-black/30 backdrop-blur-md border border-white/10 rounded-2xl p-6 compact:p-8">
              <div className="space-y-5">
                <div>
                  <h4 className="text-white font-semibold mb-3 text-sm" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Budget Overview</h4>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white/70 text-sm" style={{fontFamily: 'Inter, sans-serif'}}>Spent: $2,145</span>
                    <span className="text-white/70 text-sm" style={{fontFamily: 'Inter, sans-serif'}}>Budget: $3,000</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-3">
                    <div className="bg-gradient-to-r from-[var(--md3-primary)] to-cyan-400 h-3 rounded-full" style={{width: '71%'}}></div>
                  </div>
                  <p className="text-white/50 text-xs mt-1" style={{fontFamily: 'Inter, sans-serif'}}>71% used - $855 remaining</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <Plane size={18} className="text-sky-400 mx-auto mb-1" />
                    <p className="text-white text-sm font-semibold" style={{fontFamily: 'Inter, sans-serif'}}>$847</p>
                    <p className="text-white/50 text-xs" style={{fontFamily: 'Inter, sans-serif'}}>Flights</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <FileText size={18} className="text-rose-400 mx-auto mb-1" />
                    <p className="text-white text-sm font-semibold" style={{fontFamily: 'Inter, sans-serif'}}>$960</p>
                    <p className="text-white/50 text-xs" style={{fontFamily: 'Inter, sans-serif'}}>Hotels</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <DollarSign size={18} className="text-amber-400 mx-auto mb-1" />
                    <p className="text-white text-sm font-semibold" style={{fontFamily: 'Inter, sans-serif'}}>$218</p>
                    <p className="text-white/50 text-xs" style={{fontFamily: 'Inter, sans-serif'}}>Dining</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <Star size={18} className="text-cyan-400 mx-auto mb-1" />
                    <p className="text-white text-sm font-semibold" style={{fontFamily: 'Inter, sans-serif'}}>$120</p>
                    <p className="text-white/50 text-xs" style={{fontFamily: 'Inter, sans-serif'}}>Activities</p>
                  </div>
                </div>
                <div className="text-center pt-2">
                  <p className="text-white/50 text-xs italic" style={{fontFamily: 'Inter, sans-serif'}}>Budget tracking updates automatically as you add bookings</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* ADDITIONAL FEATURES LIST */}
      {/* ============================================ */}
      <section className="py-16 compact:py-24 px-4 compact:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 compact:mb-16">
            <h2 className="text-2xl compact:text-3xl wide:text-4xl font-bold text-white mb-4 drop-shadow-xl" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              More Tools for Every Traveler
            </h2>
            <p className="text-base compact:text-lg text-white/80 max-w-3xl mx-auto leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
              Beyond itinerary planning and AI assistance, iGo includes essential tools that make your 
              travel experience smoother from planning through to your return home.
            </p>
          </div>

          <div className="grid grid-cols-1 compact:grid-cols-2 wide:grid-cols-4 gap-4 compact:gap-6">
            <MiniFeatureCard
              icon={<FileText size={22} className="text-amber-400" />}
              title="Pass Portfolio"
              description="Store boarding passes, hotel confirmations, and travel documents. Access them offline when you need them at the airport or hotel check-in desk."
            />
            <MiniFeatureCard
              icon={<Calendar size={22} className="text-cyan-400" />}
              title="Calendar Export"
              description="Export your entire itinerary to Google Calendar with one click. Every flight, hotel check-in, and dinner reservation appears as a calendar event."
            />
            <MiniFeatureCard
              icon={<Share2 size={22} className="text-green-400" />}
              title="Trip Sharing"
              description="Share your trip plan with anyone via a unique link. Travel companions can view the full itinerary and interactive map without creating an account."
            />
            <MiniFeatureCard
              icon={<Globe size={22} className="text-[var(--md3-primary)]" />}
              title="Destination Discovery"
              description="Select any destination on your trip to see top-rated attractions, local restaurants, current weather, and city photos curated from Google Places."
            />
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* SUPPORTED BOOKING TYPES */}
      {/* ============================================ */}
      <section className="py-16 compact:py-24 px-4 compact:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 compact:mb-14">
            <h2 className="text-2xl compact:text-3xl wide:text-4xl font-bold text-white mb-4 drop-shadow-xl" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Supports Every Type of Travel Booking
            </h2>
            <p className="text-base compact:text-lg text-white/80 max-w-3xl mx-auto leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
              Whether you are flying across the Atlantic, taking the train through Europe, cruising the Caribbean, 
              or road-tripping across the country, iGo handles every booking type you might have.
            </p>
          </div>
          <div className="grid grid-cols-2 compact:grid-cols-4 gap-4">
            {[
              { icon: <Plane size={24} className="text-sky-400" />, label: 'Flights' },
              { icon: <FileText size={24} className="text-rose-400" />, label: 'Hotels' },
              { icon: <Globe size={24} className="text-purple-400" />, label: 'Vacation Rentals' },
              { icon: <Clock size={24} className="text-emerald-400" />, label: 'Trains' },
              { icon: <MapPin size={24} className="text-orange-400" />, label: 'Cruises' },
              { icon: <Star size={24} className="text-amber-400" />, label: 'Restaurants' },
              { icon: <Zap size={24} className="text-cyan-400" />, label: 'Activities' },
              { icon: <Shield size={24} className="text-green-400" />, label: 'Car Rentals' },
            ].map((type, i) => (
              <div key={i} className="bg-black/30 backdrop-blur-md border border-white/10 rounded-xl p-4 text-center hover:border-white/30 transition-all">
                <div className="mx-auto mb-2">{type.icon}</div>
                <p className="text-white text-sm font-medium" style={{fontFamily: 'Inter, sans-serif'}}>{type.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* CTA SECTION */}
      {/* ============================================ */}
      <section className="py-16 compact:py-20 px-4 compact:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-[var(--md3-primary)]/20 to-cyan-600/20 backdrop-blur-md border border-[var(--md3-primary)]/30 rounded-3xl p-8 compact:p-12">
            <h2 className="text-2xl compact:text-3xl font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Ready to Plan Your Next Adventure?
            </h2>
            <p className="text-base compact:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
              Join travelers who use iGo to organize their trips. Create up to 3 trips for free, or upgrade 
              to Premium for unlimited trips and AI-powered features. Your next great trip starts with a plan.
            </p>
            <Button 
              onClick={handleLogin}
              data-testid="cta-get-started-btn"
              className="bg-gradient-to-r from-[var(--md3-primary)] to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white text-base compact:text-lg px-8 compact:px-10 py-5 compact:py-6 rounded-full transition-all duration-300 hover:scale-105 shadow-2xl shadow-blue-500/50"
            >
              Start Planning for Free
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FAQ SECTION */}
      {/* ============================================ */}
      <section id="faq" className="py-16 compact:py-24 px-4 compact:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 compact:mb-14">
            <h2 className="text-2xl compact:text-3xl wide:text-4xl font-bold text-white mb-4 drop-shadow-xl" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Frequently Asked Questions
            </h2>
            <p className="text-base compact:text-lg text-white/80 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
              Everything you need to know about planning your trips with iGo.
            </p>
          </div>

          <div className="space-y-3">
            {faqData.map((faq, index) => (
              <FaqItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFaq === index}
                onToggle={() => toggleFaq(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* ABOUT SECTION */}
      {/* ============================================ */}
      <section className="py-16 compact:py-24 px-4 compact:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-black/30 backdrop-blur-md border border-white/10 rounded-2xl p-8 compact:p-12">
            <h2 className="text-2xl compact:text-3xl font-bold text-white mb-6 text-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              About iGo
            </h2>
            <div className="space-y-4 text-white/80 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
              <p>
                iGo was built out of frustration with how disorganized travel planning can be. After one too many 
                trips where booking confirmations were scattered across dozens of emails, restaurant recommendations 
                were lost in group chats, and budgets were tracked on crumpled napkins, we decided to build something better.
              </p>
              <p>
                The result is a travel planner that actually understands how modern travelers book trips: through 
                email confirmations, online reservations, and last-minute changes. Instead of forcing you to manually 
                enter every detail, iGo reads your booking emails and builds your itinerary automatically. Then it 
                goes further with AI-powered suggestions, interactive maps, and collaboration tools that make 
                group trip planning painless.
              </p>
              <p>
                Whether you are a solo backpacker planning your next hostel-hopping adventure, a couple organizing 
                a honeymoon across multiple countries, or a family coordinating a complex trip with flights, hotels, 
                activities, and dining reservations, iGo adapts to how you travel. The free plan lets you plan up 
                to three trips, and Premium unlocks unlimited trips plus AI features for just a one-time payment.
              </p>
              <p>
                iGo is available as a web app and as a native Android application, so you can access your trip 
                plans on any device. Your data is synced across all your devices through your Google account, 
                and important documents like boarding passes are cached for offline access when you need them most.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOOTER */}
      {/* ============================================ */}
      <footer className="border-t border-white/10 py-8 compact:py-12">
        <div className="max-w-7xl mx-auto px-4 compact:px-6">
          <div className="grid grid-cols-1 compact:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="text-white" size={22} />
                <span className="text-lg font-bold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>{t('appName')}</span>
              </div>
              <p className="text-sm text-white/60 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
                AI-powered trip planning with automatic email parsing, smart itineraries, interactive maps, and budget tracking.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Product</h4>
              <div className="space-y-2">
                <a href="#features" className="block text-sm text-white/60 hover:text-white transition-colors" style={{fontFamily: 'Inter, sans-serif'}}>Features</a>
                <a href="#how-it-works" className="block text-sm text-white/60 hover:text-white transition-colors" style={{fontFamily: 'Inter, sans-serif'}}>How It Works</a>
                <a href="#faq" className="block text-sm text-white/60 hover:text-white transition-colors" style={{fontFamily: 'Inter, sans-serif'}}>FAQ</a>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Legal</h4>
              <div className="space-y-2">
                <Link to="/privacy" className="block text-sm text-white/60 hover:text-white transition-colors" style={{fontFamily: 'Inter, sans-serif'}} data-testid="privacy-policy-link">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="block text-sm text-white/60 hover:text-white transition-colors" style={{fontFamily: 'Inter, sans-serif'}} data-testid="terms-of-service-link">
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 text-center">
            <p className="text-sm text-white/50" style={{fontFamily: 'Inter, sans-serif'}}>
              © {new Date().getFullYear()} {t('appName')}. {t('builtWith')}
            </p>
          </div>
        </div>
      </footer>
    </RotatingBackground>
  );
};

/* ============================================ */
/* SUB-COMPONENTS */
/* ============================================ */

const FeatureCard = ({ icon, title, description, isMobile }) => (
  <div className="group bg-[var(--md3-surface-container-lowest)]/20 backdrop-blur-md border border-white/20 rounded-2xl p-5 compact:p-8 hover:bg-[var(--md3-surface-container-lowest)]/30 hover:border-[var(--md3-primary)]/50 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-[var(--md3-primary)]/30">
    <div className="mb-3 compact:mb-4 transform group-hover:scale-110 transition-transform duration-300">
      <div className="w-12 h-12 compact:w-14 compact:h-14 rounded-xl bg-white/10 flex items-center justify-center">
        {icon}
      </div>
    </div>
    <h3 className="text-lg compact:text-xl font-bold text-white mb-2 compact:mb-3" style={{fontFamily: 'Space Grotesk, sans-serif'}}>{title}</h3>
    <p className="text-sm compact:text-base text-white/80 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>{description}</p>
  </div>
);

const StepCard = ({ number, icon, title, description }) => (
  <div className="text-center">
    <div className="relative inline-flex items-center justify-center w-16 h-16 compact:w-20 compact:h-20 rounded-full bg-gradient-to-br from-[var(--md3-primary)] to-cyan-600 mb-6 shadow-lg shadow-blue-500/30">
      <span className="text-white">{icon}</span>
      <span className="absolute -top-1 -right-1 w-7 h-7 bg-white text-[var(--md3-primary)] rounded-full flex items-center justify-center text-sm font-bold shadow-md" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
        {number}
      </span>
    </div>
    <h3 className="text-lg compact:text-xl font-bold text-white mb-3" style={{fontFamily: 'Space Grotesk, sans-serif'}}>{title}</h3>
    <p className="text-sm compact:text-base text-white/80 leading-relaxed max-w-sm mx-auto" style={{fontFamily: 'Inter, sans-serif'}}>{description}</p>
  </div>
);

const MiniFeatureCard = ({ icon, title, description }) => (
  <div className="bg-black/30 backdrop-blur-md border border-white/10 rounded-2xl p-5 compact:p-6 hover:border-white/30 transition-all">
    <div className="mb-3">{icon}</div>
    <h4 className="text-base font-bold text-white mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>{title}</h4>
    <p className="text-sm text-white/70 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>{description}</p>
  </div>
);

const FaqItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="bg-black/30 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 compact:p-5 text-left hover:bg-white/5 transition-colors"
    >
      <span className="text-white font-medium text-sm compact:text-base pr-4" style={{fontFamily: 'Inter, sans-serif'}}>{question}</span>
      {isOpen ? <ChevronUp size={20} className="text-white/60 flex-shrink-0" /> : <ChevronDown size={20} className="text-white/60 flex-shrink-0" />}
    </button>
    {isOpen && (
      <div className="px-4 compact:px-5 pb-4 compact:pb-5">
        <p className="text-sm text-white/70 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>{answer}</p>
      </div>
    )}
  </div>
);

/* ============================================ */
/* FAQ DATA */
/* ============================================ */

const faqData = [
  {
    question: "Is iGo free to use?",
    answer: "Yes! iGo offers a free plan that lets you create and manage up to 3 trips with full itinerary management, interactive maps, weather forecasts, and trip sharing. Premium features like AI-powered email parsing, the AI travel assistant, and unlimited trips are available with a one-time Premium upgrade."
  },
  {
    question: "How does the email parsing work?",
    answer: "When you connect your Gmail account, iGo securely scans your inbox for travel booking confirmation emails using AI. It recognizes emails from airlines, hotels, rental companies, trains, and cruise lines, then automatically extracts the booking details like confirmation numbers, dates, costs, and locations. You can also paste booking email text directly if you prefer not to connect Gmail."
  },
  {
    question: "What types of bookings does iGo support?",
    answer: "iGo supports all major travel booking types: flights (with departure/arrival airports and seat assignments), hotels, vacation rentals (Airbnb, VRBO), trains, cruises (with port stops), restaurants, activities and tours, and car rentals. Each type has specialized fields and displays differently on the map with color-coded markers."
  },
  {
    question: "Can I share my trip with others?",
    answer: "Absolutely. You can generate a shareable link for any trip that lets anyone view your complete itinerary and interactive map without needing to create an iGo account. This is perfect for sharing travel plans with family, friends, or travel companions so everyone stays on the same page."
  },
  {
    question: "Is my data secure?",
    answer: "iGo uses Google OAuth 2.0 for authentication, which means we never see or store your Google password. Gmail access is read-only and limited to scanning for booking emails. All data is transmitted over HTTPS encryption, and you can disconnect your Gmail at any time from the app settings. We use Sentry for error monitoring and follow security best practices."
  },
  {
    question: "Does iGo work offline?",
    answer: "Yes, iGo caches your trip data and documents (like boarding passes) locally on your device using IndexedDB. When you lose internet connectivity, you can still access your trip details and stored documents. This is especially useful when traveling through areas with limited connectivity or when you need to show a boarding pass at the airport."
  },
  {
    question: "What is the AI travel assistant?",
    answer: "The AI travel assistant is a built-in feature powered by Google Gemini that provides personalized travel recommendations based on your specific trip. It can suggest restaurants near your hotel, recommend activities for free time in your schedule, optimize your itinerary to reduce transit time, provide destination-specific budget tips, and generate a customized packing list. It has full context of your trip details, destinations, and existing bookings."
  },
  {
    question: "Is there a mobile app?",
    answer: "iGo is available as a web application that works on any device with a browser, and also as a native Android app built with Capacitor. The Android app offers the same full feature set as the web version, plus native capabilities like camera access for scanning documents and optimized performance for mobile use."
  },
  {
    question: "How much does Premium cost?",
    answer: "iGo Premium is a one-time payment of $4.99, not a subscription. This gives you lifetime access to unlimited trips, AI-powered email parsing, the AI travel assistant (suggestions, optimization, budget tips, packing lists, and chat), and priority support. There are no recurring charges."
  },
  {
    question: "Can I export my itinerary to my calendar?",
    answer: "Yes, if you have connected your Gmail account, you can export your entire trip itinerary to Google Calendar with one click. Each booking becomes a separate calendar event with all the relevant details like confirmation numbers, locations, and times. This makes it easy to see your travel plans alongside your regular schedule."
  }
];

export default LandingPage;
